import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Row, Col, Card, Button, Form, Spinner, Table, Badge } from "react-bootstrap";
import Select from "react-select";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import SignatureModal from "../Common/SignatureModal";

const num = (v) => { const n = Number(v); return isFinite(n) ? n : 0; };
const calcQty = (m) =>
 Number((num(m.nos || 1) * num(m.length) * (num(m.breadth) || 1) * (num(m.height) || 1)).toFixed(4));
const getItemKey = (item) =>
 [item.itemName || "", item.unit || "", item.description || ""]
 .map((v) => String(v).trim().toLowerCase()).filter(Boolean).join("|");
const emptyMeasurement = () => ({ location: "", nos: 1, length: 0, breadth: 0, height: 0, remarks: "" });

const ContractorJMRAdd = () => {
 const navigate = useNavigate();
 const [searchParams] = useSearchParams();
 const cwoFromUrl = searchParams.get("contractualWorkOrder") || "";
 const workOrderFromUrl = searchParams.get("workOrder") || "";

 const [cwos, setCwos] = useState([]);
 const [selectedCwoId, setSelectedCwoId] = useState(cwoFromUrl);
 const [selectedCwo, setSelectedCwo] = useState(null);
 const [existingJMRs, setExistingJMRs] = useState([]);
 const [availableItems, setAvailableItems] = useState([]);
 const [selectedItemKeys, setSelectedItemKeys] = useState([]);
 const [items, setItems] = useState([]);

 const [measurementDate, setMeasurementDate] = useState(new Date().toISOString().slice(0, 10));
 const [remarks, setRemarks] = useState("");
 const [jmrFile, setJmrFile] = useState(null);

 const [showSignModal, setShowSignModal] = useState(false);
 const [pendingFormData, setPendingFormData] = useState(null);
 const [loading, setLoading] = useState(true);
 const [saving, setSaving] = useState(false);

 // Load contractual work orders
 useEffect(() => {
 const load = async () => {
 try {
 setLoading(true);
 const query = new URLSearchParams({ limit: "1000" });
 if (workOrderFromUrl) query.set("workOrder", workOrderFromUrl);
 const res = await api.get(`/contractual-work-orders?${query.toString()}`);
 if (res.data.success) {
 const list = res.data.data || [];
 setCwos(list);
 const match =
 list.find((c) => c._id === cwoFromUrl) ||
 (workOrderFromUrl
 ? list.find((c) => String(c.workOrder?._id || c.workOrder || "") === String(workOrderFromUrl))
 : null) ||
 list[0];
 if (match) setSelectedCwoId(match._id);
 }
 } catch {
 swal("Error", "Failed to load contractual work orders", "error");
 } finally {
 setLoading(false);
 }
 };
 load();
 }, [cwoFromUrl, workOrderFromUrl]);

 useEffect(() => {
 setSelectedCwo(cwos.find((c) => c._id === selectedCwoId) || null);
 setSelectedItemKeys([]);
 setItems([]);
 }, [selectedCwoId, cwos]);

 // Load existing JMRs for this CWO to calculate remaining quantities
 useEffect(() => {
 const loadJMRs = async () => {
 if (!selectedCwoId) { setExistingJMRs([]); return; }
 try {
 const res = await api.get(`/contractor-jmrs?contractualWorkOrder=${selectedCwoId}&limit=1000`);
 if (res.data.success) setExistingJMRs(res.data.data || []);
 } catch {
 setExistingJMRs([]);
 }
 };
 loadJMRs();
 }, [selectedCwoId]);

 const measuredQuantityMap = useMemo(() => {
 const map = new Map();
 existingJMRs.filter((j) => j.approvalStatus !== "Rejected").forEach((j) => {
 (j.items || []).forEach((item) => {
 const key = getItemKey(item);
 map.set(key, num(map.get(key)) + num(item.measuredQuantity));
 });
 });
 return map;
 }, [existingJMRs]);

 // Build available items from CWO's selectedBoqItems
 useEffect(() => {
 const next = (selectedCwo?.selectedBoqItems || [])
 .map((item) => {
 const woQty = num(item.quantity);
 const prevQty = num(measuredQuantityMap.get(getItemKey(item)));
 const remaining = Math.max(woQty - prevQty, 0);
 return { ...item, woQuantity: woQty, previousQuantity: prevQty, remainingQuantity: remaining };
 })
 .filter((item) => item.remainingQuantity > 0);
 setAvailableItems(next);
 }, [selectedCwo, measuredQuantityMap]);

 const itemOptions = useMemo(() =>
 availableItems.map((item) => ({
 value: getItemKey(item),
 label: `${item.itemName}${item.description ? ` — ${item.description}` : ""} (Remaining: ${item.remainingQuantity} ${item.unit || ""})`,
 item,
 })),
 [availableItems]
 );

 const handleItemSelect = (opts) => {
 const selected = opts || [];
 setSelectedItemKeys(selected.map((o) => o.value));
 setItems((prev) => {
 const prevMap = new Map(prev.map((it) => [getItemKey(it), it]));
 return selected.map((o) => {
 const key = o.value;
 if (prevMap.has(key)) return prevMap.get(key);
 return { ...o.item, measurements: [emptyMeasurement()] };
 });
 });
 };

 const totalMeasured = (item) =>
 Number(item.measurements.reduce((s, m) => s + calcQty(m), 0).toFixed(4));

 const currentRemaining = (item) =>
 Number(Math.max(item.remainingQuantity - totalMeasured(item), 0).toFixed(4));

 const updateMeasRow = (itemIdx, rowIdx, field, value) =>
 setItems((prev) => prev.map((item, i) => {
 if (i !== itemIdx) return item;
 const measurements = item.measurements.map((m, j) => {
 if (j !== rowIdx) return m;
 let val = value;
 if (field === "length") {
 const currentTotal = item.measurements.reduce((s, r, ri) => ri === rowIdx ? s : s + calcQty(r), 0);
 const maxAllowed = Math.max(item.remainingQuantity - currentTotal, 0);
 val = Math.min(Math.max(num(value), 0), maxAllowed);
 }
 return { ...m, [field]: val };
 });
 return { ...item, measurements };
 }));

 const addMeasRow = (itemIdx) =>
 setItems((prev) => prev.map((item, i) =>
 i === itemIdx ? { ...item, measurements: [...item.measurements, emptyMeasurement()] } : item
 ));

 const removeMeasRow = (itemIdx, rowIdx) =>
 setItems((prev) => prev.map((item, i) => {
 if (i !== itemIdx) return item;
 const measurements = item.measurements.filter((_, j) => j !== rowIdx);
 return { ...item, measurements: measurements.length ? measurements : [emptyMeasurement()] };
 }));

 const handleSubmit = async (e) => {
 e.preventDefault();
 if (!selectedCwoId) return swal("Error", "Please select a contractual work order", "error");
 if (!measurementDate) return swal("Error", "Measurement date is required", "error");
 if (!items.length) return swal("Error", "Please select at least one item", "error");

 const validItems = items
 .filter((item) => totalMeasured(item) > 0)
 .map((item) => ({ ...item, measurements: item.measurements.filter((m) => calcQty(m) > 0) }));

 if (!validItems.length) return swal("Error", "At least one item must have measured quantity", "error");

 const overQty = validItems.find((item) => totalMeasured(item) > item.remainingQuantity);
 if (overQty)
 return swal("Error", `${overQty.itemName} measured quantity exceeds remaining (${overQty.remainingQuantity})`, "error");

 setPendingFormData({ validItems });
 setShowSignModal(true);
 };

 const handleSignatureSave = async (sigFile) => {
 setShowSignModal(false);
 if (!pendingFormData) return;
 try {
 setSaving(true);
 const data = new FormData();
 data.append("contractualWorkOrder", selectedCwoId);
 data.append("measurementDate", measurementDate);
 data.append("remarks", remarks);
 data.append("items", JSON.stringify(pendingFormData.validItems));
 if (jmrFile) data.append("jmrDocument", jmrFile);
 if (sigFile) data.append("submittedBySign", sigFile);

 const res = await api.post("/contractor-jmrs", data, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 if (res.data.success) {
 swal("Success", "Contractor JMR created successfully", "success");
 navigate("/contractor-jmr");
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to create Contractor JMR", "error");
 } finally {
 setSaving(false);
 setPendingFormData(null);
 }
 };

 if (loading) return <div className="text-center py-5"><Spinner /></div>;

 return (
 <Fragment>
 <PageTitle activeMenu="Add Contractor JMR" motherMenu="Commercial" />
 <Form onSubmit={handleSubmit}>
 <Card className="shadow-sm border-0">
 <Card.Header className="bg-white d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">Create Contractor JMR</Card.Title>
 <Button variant="light" size="sm" onClick={() => navigate("/contractor-jmr")}>Back</Button>
 </Card.Header>

 <Card.Body>
 <Row className="g-3 mb-3">
 <Col md={5}>
 <Form.Label>Contractual Work Order <span className="text-danger">*</span></Form.Label>
 <Form.Select value={selectedCwoId} onChange={(e) => setSelectedCwoId(e.target.value)}>
 <option value="">Select CWO</option>
 {cwos.map((cwo) => (
 <option key={cwo._id} value={cwo._id}>
 {cwo.cwoNo || cwo._id}
 {cwo.vendor?.name ? ` — ${cwo.vendor.name}` : ""}
 </option>
 ))}
 </Form.Select>
 </Col>
 <Col md={3}>
 <Form.Label>Contractor</Form.Label>
 <div className="form-control bg-light fw-bold">{selectedCwo?.vendor?.name || "—"}</div>
 </Col>
 <Col md={4}>
 <Form.Label>Measurement Date <span className="text-danger">*</span></Form.Label>
 <Form.Control
 type="date"
 value={measurementDate}
 onChange={(e) => setMeasurementDate(e.target.value)}
 required
 />
 </Col>
 <Col md={4}>
 <Form.Label>JMR Document Upload</Form.Label>
 <Form.Control
 type="file"
 accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
 onChange={(e) => setJmrFile(e.target.files?.[0] || null)}
 />
 </Col>
 <Col md={12}>
 <Form.Label>Remarks</Form.Label>
 <Form.Control
 as="textarea"
 rows={2}
 placeholder="Any notes or remarks..."
 value={remarks}
 onChange={(e) => setRemarks(e.target.value)}
 />
 </Col>
 </Row>

 {/* Item Selection */}
 <div className="border rounded p-3 mb-3 bg-light">
 <Form.Label className="fw-bold mb-2">
 Select Items <span className="text-danger">*</span>
 {availableItems.length > 0 && (
 <span className="ms-2 text-muted fw-normal small">({availableItems.length} available)</span>
 )}
 </Form.Label>
 <Select
 isMulti
 options={itemOptions}
 value={itemOptions.filter((o) => selectedItemKeys.includes(o.value))}
 onChange={handleItemSelect}
 placeholder={selectedCwo ? "Select items to measure..." : "Select a contractual work order first"}
 isDisabled={!selectedCwo || availableItems.length === 0}
 closeMenuOnSelect={false}
 classNamePrefix="react-select"
 noOptionsMessage={() =>
 selectedCwo ? "All items fully measured" : "Select a contractual work order first"
 }
 />
 </div>

 {/* Items Table */}
 {items.length > 0 && (
 <div className="table-responsive">
 <Table bordered className="align-middle">
 <thead className="table-light">
 <tr>
 <th style={{ width: 40 }}>#</th>
 <th>Item</th>
 <th style={{ width: 70 }}>Unit</th>
 <th style={{ width: 80 }}>CWO Qty</th>
 <th style={{ width: 75 }}>Prev Measured</th>
 <th style={{ width: 90 }}>Remaining</th>
 <th style={{ width: 140 }}>Measured Qty</th>
 <th style={{ width: 36 }}></th>
 </tr>
 </thead>
 <tbody>
 {items.map((item, itemIdx) =>
 item.measurements.map((m, rowIdx) => (
 <React.Fragment key={`${itemIdx}-${rowIdx}`}>
 {/* Quantity row */}
 <tr>
 {rowIdx === 0 && (
 <>
 <td rowSpan={item.measurements.length * 2 + 1} className="text-center align-middle">
 {itemIdx + 1}
 </td>
 <td rowSpan={item.measurements.length * 2 + 1} className="align-middle">
 <strong>{item.itemName}</strong>
 {item.description && <div className="small text-muted">{item.description}</div>}
 </td>
 <td rowSpan={item.measurements.length * 2 + 1} className="text-center align-middle">
 {item.unit || "—"}
 </td>
 <td rowSpan={item.measurements.length * 2 + 1} className="text-center align-middle">
 {item.woQuantity}
 </td>
 <td rowSpan={item.measurements.length * 2 + 1} className="text-center align-middle text-danger">
 {item.previousQuantity}
 </td>
 <td rowSpan={item.measurements.length * 2 + 1} className="text-center align-middle fw-bold text-success">
 {currentRemaining(item)}
 </td>
 </>
 )}
 <td>
 <Form.Control
 type="number" size="sm" step="any" min="0"
 max={item.remainingQuantity}
 value={m.length}
 onChange={(e) => updateMeasRow(itemIdx, rowIdx, "length", e.target.value)}
 />
 </td>
 <td className="text-center">
 <Button
 variant="outline-danger" size="sm" style={{ padding: "2px 6px" }}
 onClick={() => removeMeasRow(itemIdx, rowIdx)}
 >
 <i className="fa fa-trash"></i>
 </Button>
 </td>
 </tr>
 {/* Location + Total row */}
 <tr>
 <td className="pt-0 pb-1 px-2" style={{ borderTop: "none" }}>
 <Form.Control
 as="textarea"
 rows={2}
 placeholder="Location / remarks..."
 value={m.location}
 onChange={(e) => updateMeasRow(itemIdx, rowIdx, "location", e.target.value)}
 style={{ resize: "vertical", minHeight: "52px" }}
 />
 </td>
 <td className="text-center align-middle" style={{ borderTop: "none" }}>
 </td>
 </tr>
 </React.Fragment>
 )).concat(
 <tr key={`${itemIdx}-footer`} className="table-secondary">
 <td className="ps-2" style={{ background: "transparent" }}>
 <Button
 variant="link"
 size="sm"
 className="text-primary p-0 text-decoration-none fw-semibold"
 onClick={() => addMeasRow(itemIdx)}
 >
 <i className="fa fa-plus me-1"></i> Add Row
 </Button>
 </td>
 <td className="text-center fw-bold text-primary" style={{ background: "transparent" }}>
 Total: {totalMeasured(item)}
 {totalMeasured(item) > item.remainingQuantity && (
 <Badge bg="danger" className="ms-1">Over!</Badge>
 )}
 </td>
 </tr>
 )
 )}
 </tbody>
 </Table>
 </div>
 )}

 {items.length === 0 && selectedCwo && (
 <div className="text-center text-muted py-3">
 Select items above to start entering measurements.
 </div>
 )}
 </Card.Body>

 <Card.Footer className="text-end bg-white">
 <Button variant="secondary" className="me-2" onClick={() => navigate("/contractor-jmr")}>
 Cancel
 </Button>
 <Button type="submit" disabled={saving || !selectedCwoId || !items.length}>
 {saving ? <><Spinner size="sm" className="me-2" />Saving...</> : "Save Contractor JMR"}
 </Button>
 </Card.Footer>
 </Card>
 </Form>

 <SignatureModal
 show={showSignModal}
 onHide={() => { setShowSignModal(false); setPendingFormData(null); }}
 onSave={handleSignatureSave}
 title="Submitted By — Signature Required"
 />
 </Fragment>
 );
};

export default ContractorJMRAdd;
